import { StyledProps } from '@front10';
import React, { useState } from 'react';
import { isNilOrEmpty } from '../../../helpers/utils';
import Box from '../../elements/Box';
import Address from '../_commons/stationItemAtoms/Address';
import CallToAction from '../_commons/stationItemAtoms/CallToAction';
import MoreInformationCallToAction from '../_commons/stationItemAtoms/MoreInformationCallToAction';
import DepartureArriveInfo from '../_commons/stationItemAtoms/DepartureArriveInfo';
import Directions from '../_commons/stationItemAtoms/Directions';
import HoursOperation from '../_commons/stationItemAtoms/HoursOperation';
import LocationDescriptionText from '../_commons/stationItemAtoms/LocationDescriptionText';
import LocationType from '../_commons/stationItemAtoms/LocationType';
import ParkingInfo from '../_commons/stationItemAtoms/ParkingInfo';
import SpecialInstructions from '../_commons/stationItemAtoms/SpecialInstructions';
import TelephoneNumbers from '../_commons/stationItemAtoms/TelephoneNumbers';
import TicketType from '../_commons/stationItemAtoms/TicketType';
import { isSSR } from '@front10/utils';

interface Props {
  showDepartureArriveInfo: boolean;
  showAddress: boolean;
  showLocationType: boolean;
  showSpecialInstructions: boolean;
  showTicketTypes: boolean;
  showTelephoneNumbers: boolean;
  showMoreInformationCallToAction: boolean;
  showHoursOperation: boolean;
  showCallToAction: boolean;
  showDirections: boolean;
  showParkingInfo: boolean;
  showLocationDescriptionText: boolean;
  departureArriveInfoStyles: StyledProps;
  moreInformationCallToActionStyles: StyledProps;
  addressStyles: StyledProps;
  specialInstructionsStyles: StyledProps;
  locationTypeStyles: StyledProps;
  ticketTypeStyles: StyledProps;
  telephoneNumbersStyles: StyledProps;
  hoursOperationStyles: StyledProps;
  callToActionStyles: StyledProps;
  directionsStyles: StyledProps;
  parkingInfoStyles: StyledProps;
  locationDescriptionTextStyles: StyledProps;
  data: $TSFixMe;
  open: boolean;
  onFocus: (data: $TSFixMe) => void;
  onStationClick: (data: $TSFixMe) => void;
}

function StationItem({
  showDepartureArriveInfo = true,
  showAddress = true,
  showSpecialInstructions = true,
  showTicketTypes = true,
  showTelephoneNumbers = true,
  showHoursOperation = true,
  showLocationType = true,
  showCallToAction = false,
  showDirections = true,
  showParkingInfo = true,
  showLocationDescriptionText = true,
  showMoreInformationCallToAction = true,
  moreInformationCallToActionStyles = {},
  departureArriveInfoStyles,
  addressStyles,
  specialInstructionsStyles,
  locationTypeStyles,
  ticketTypeStyles,
  telephoneNumbersStyles,
  callToActionStyles,
  hoursOperationStyles,
  directionsStyles,
  parkingInfoStyles,
  locationDescriptionTextStyles,
  open = false,
  data = {},
  onFocus = () => {},
  onStationClick,
  ...props
}: StyledProps<Props>) {
  const [isOpen, setIsOpen] = useState(open);

  const handleOpen = (evt: $TSFixMe) => {
    if (showCallToAction) {
      setIsOpen(true);
    } else if (!evt.target.href && !isOpen) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    return onFocus(data);
  };

  return (
    <Box
      shadow="@theme:card.shadow"
      hover="light-7"
      rounded="@theme:card.rounded"
      fontName="@theme:card.fontName"
      border={true}
      padding="2"
      fontSize="3"
      textAlign="left"
      {...props}
      tabIndex={0}
      className="station-item"
      onClick={!showCallToAction ? handleOpen : undefined}
      onFocus={handleFocus}
      {...(!showCallToAction && !isOpen && { cursor: 'pointer' })}
      dataTestId="stationItemLayoutRow"
    >
      {showDepartureArriveInfo && (
        <DepartureArriveInfo
          {...data}
          {...departureArriveInfoStyles}
          onStationClick={onStationClick}
        />
      )}
      {showAddress && <Address {...data} {...addressStyles} />}
      {/* show this info if the card is open */}
      {isOpen ? (
        <>
          {showLocationType && !isNilOrEmpty(data.metaData?.locationType) && (
            <LocationType {...data} {...locationTypeStyles} data={data.metaData.locationType} />
          )}
          {showSpecialInstructions && !isNilOrEmpty(data.specialInstructions) && (
            <SpecialInstructions {...data} {...specialInstructionsStyles} />
          )}
          {showTicketTypes && !isNilOrEmpty(data.ticketSaleNotes) && (
            <TicketType {...data} {...ticketTypeStyles} />
          )}
          {showTelephoneNumbers && data.phoneNumbers && (
            <TelephoneNumbers {...data} {...telephoneNumbersStyles} />
          )}
          {showHoursOperation && <HoursOperation {...data} {...hoursOperationStyles} />}
          {showDirections && !isNilOrEmpty(data.directions) && (
            <Directions {...data} {...directionsStyles} />
          )}
          {showParkingInfo && !isNilOrEmpty(data.parkingInfo) && (
            <ParkingInfo {...data} {...parkingInfoStyles} />
          )}
          {showLocationDescriptionText && !isNilOrEmpty(data.locationDescriptionText) && (
            <LocationDescriptionText {...data} {...locationDescriptionTextStyles} />
          )}
          {showMoreInformationCallToAction && !isSSR() && (
            <MoreInformationCallToAction
              {...data}
              moreInformationCallToActionStyles={moreInformationCallToActionStyles}
              onStationClick={onStationClick}
            />
          )}
        </>
      ) : (
        <>{showCallToAction && <CallToAction onClick={handleOpen} {...callToActionStyles} />}</>
      )}
    </Box>
  );
}

StationItem.componentName = 'StationItem';

export default StationItem;
